import React, { useCallback, useEffect, useState } from 'react';
import Header from '../../components/Header';
import MartyrSelect from '../../components/MartyrSelect';
import { poemsApi } from '../../services/api';

const emptyForm = {
  martyr_id: '',
  title: '',
  poet_name: '',
  content: ''
};

const AdminPoemsPage = () => {
  const token = localStorage.getItem('adminToken');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const data = await poemsApi.getAll({ limit: 50 }, token);
      setItems(data.poems || []);
    } catch (err) {
      setError(err.message || 'تعذر تحميل القصائد');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      window.location.href = '/admin/login';
      return;
    }
    fetchItems();
  }, [token, fetchItems]);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      const payload = {
        ...form,
        martyr_id: form.martyr_id ? parseInt(form.martyr_id, 10) : null
      };
      if (editingId) {
        await poemsApi.update(editingId, payload, token);
        setSuccess('تم تحديث القصيدة بنجاح');
      } else {
        await poemsApi.create(payload, token);
        setSuccess('تمت إضافة القصيدة بنجاح');
      }
      resetForm();
      fetchItems();
    } catch (err) {
      setError(err.message || 'تعذر حفظ القصيدة');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setForm({
      martyr_id: item.martyr_id ? String(item.martyr_id) : '',
      title: item.title || '',
      poet_name: item.poet_name || '',
      content: item.content || ''
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('هل تريد حذف هذه القصيدة؟')) return;
    try {
      await poemsApi.delete(id, token);
      fetchItems();
    } catch (err) {
      setError(err.message || 'تعذر الحذف');
    }
  };

  return (
    <div className="min-h-screen bg-white" dir="rtl">
      <Header />
      <main className="section-padding">
        <div className="container-responsive max-w-5xl">
          <h1 className="text-responsive-3xl font-bold text-primary-800 mb-2">القصائد</h1>
          <p className="text-gray-600 mb-8">اترك اسم الشهيد فارغاً للقصائد العامة في القائمة الرئيسية، أو اختر شهيداً لتظهر في زر «القصائد» داخل بطاقته. اسم الشاعر اختياري إذا كان غير معروف.</p>

          {error && <div className="mb-4 p-4 rounded-lg bg-red-50 text-red-700">{error}</div>}
          {success && <div className="mb-4 p-4 rounded-lg bg-green-50 text-green-700">{success}</div>}

          <div className="card mb-8">
            <div className="card-body space-y-4">
              <h2 className="text-xl font-bold text-primary-800">
                {editingId ? 'تعديل القصيدة' : 'إضافة قصيدة جديدة'}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="form-group">
                  <label className="form-label">اسم الشهيد (اختياري)</label>
                  <MartyrSelect
                    value={form.martyr_id}
                    onChange={(value) => setForm((prev) => ({ ...prev, martyr_id: value }))}
                    token={token}
                    required={false}
                    emptyLabel="قصيدة عامة — تظهر في صفحة القصائد"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="form-group">
                    <label className="form-label">عنوان القصيدة</label>
                    <input
                      className="form-input"
                      value={form.title}
                      onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">اسم الشاعر (اختياري)</label>
                    <input
                      className="form-input"
                      value={form.poet_name}
                      onChange={(e) => setForm((prev) => ({ ...prev, poet_name: e.target.value }))}
                      placeholder="اتركه فارغاً إذا كان الشاعر غير معروف"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">نص القصيدة</label>
                  <textarea
                    className="form-input min-h-[240px] poem-verse text-right"
                    value={form.content}
                    onChange={(e) => setForm((prev) => ({ ...prev, content: e.target.value }))}
                    placeholder={'اكتب الأبيات هنا...\nكل بيت في سطر'}
                    required
                  />
                </div>
                <div className="flex gap-3">
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? 'جاري الحفظ...' : editingId ? 'تحديث' : 'إضافة'}
                  </button>
                  {editingId && (
                    <button type="button" className="btn btn-secondary" onClick={resetForm}>إلغاء</button>
                  )}
                </div>
              </form>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <h2 className="text-xl font-bold text-primary-800 mb-4">القصائد المضافة</h2>
              {loading ? (
                <p>جاري التحميل...</p>
              ) : items.length === 0 ? (
                <p className="text-gray-500">لا توجد قصائد بعد</p>
              ) : (
                <div className="space-y-4">
                  {items.map((item) => (
                    <div key={item.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between gap-4">
                        <div>
                          <h3 className="font-bold text-lg">{item.title}</h3>
                          {item.poet_name ? (
                            <p className="poet-name text-primary-800 mt-1">الشاعر: {item.poet_name}</p>
                          ) : (
                            <p className="text-sm text-gray-500 mt-1">شاعر غير معروف</p>
                          )}
                          <p className="text-sm text-gray-600">
                            {item.martyr_name_ar ? `عن الشهيد: ${item.martyr_name_ar}` : 'قصيدة عامة'}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <button className="btn btn-ghost btn-sm" onClick={() => handleEdit(item)}>تعديل</button>
                          <button className="btn btn-error btn-sm" onClick={() => handleDelete(item.id)}>حذف</button>
                        </div>
                      </div>
                      <p className="poem-verse mt-4 text-gray-800 line-clamp-4">{item.content}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminPoemsPage;
